/* eslint-disable no-magic-numbers */
import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import Fuse from 'fuse.js'
import classNames from 'classnames'
import { forceCheck } from 'react-lazyload'
import { Checkbox, Container, Heading, Icon } from '@scentregroup/ui'
import Select from '../../components/select'
import debounce from '../../helpers/debounce'
import { storeAcceptsGiftCard } from '../../helpers/store-helper'
import analyticsUtils from '../../helpers/analytics-utils'
import Header from '../../components/header'
import { AFTERPAY_TEXT } from '../../constants'
import SearchField from '../search-field'
import { AgentInfoContext } from '../../context'
import VisibleStores from './visible-stores'
import style from './index.scss'

const categoryToOption = ({ title, slug, level }) => ({
  label: title,
  value: slug,
  level,
})

const optionLabel = ({ label }) => label

class StoreList extends Component {
  state = {
    stores: this.props.stores,
    searchValue: '',
    retailCategory: '',
    subRetailCategory: '',
    giftCardFilter: false,
    afterpayFilter: false,
    option: null,
  }

  componentDidMount() {
    const {
      'gift-cards-accepted': giftCardFilter,
      'after-pay': afterpayFilter,
    } = this.props.filters

    this.filterList({
      ...(giftCardFilter && { giftCardFilter }),
      ...(afterpayFilter && { afterpayFilter }),
    })
  }

  componentDidUpdate(prev) {
    const { category } = this.props

    if (prev.category !== category) {
      this.filterList({ category })
    }

    // if you can figure out how to mock this function in jest, go for it.
    if (forceCheck) {
      forceCheck()
    }
  }
  analyticsAction = eventAction => {
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.STORE_SEARCH,
      eventAction,
    })
  }
  createAddress = (
    {
      category = this.props.category,
      giftCardFilter = this.state.giftCardFilter,
      afterpayFilter = this.state.afterpayFilter,
    },
    ...options
  ) =>
    // need to use undefined to unset parameter. false / null / '' won't do
    // https://www.npmjs.com/package/query-string#falsy-values
    this.props.createAddress(
      {
        path: this.props.centreCategories.pathOf(category),
        filters: {
          'gift-cards-accepted': giftCardFilter || undefined,
          'after-pay': afterpayFilter || undefined,
        },
      },
      ...options
    )

  filterList = ({
    searchValue = this.state.searchValue,
    giftCardFilter = this.state.giftCardFilter,
    afterpayFilter = this.state.afterpayFilter,
    category = this.props.category,
  } = {}) => {
    const {
      retailCategory,
      subRetailCategory,
      category: storeCategory,
    } = this.calculateCategories(category)

    this.createAddress(
      { category, giftCardFilter, afterpayFilter },
      { navigate: true }
    )

    const stores = this.storeMatchesSearch(
      searchValue,

      this.props.stores
        .filter(
          store =>
            (!giftCardFilter || storeAcceptsGiftCard(store.paymentMethods)) &&
            (!storeCategory ||
              this.storeInCategory(store.sys.id, storeCategory))
        )
        .filter(store =>
          afterpayFilter ? store.paymentMethods.includes('Afterpay') : store
        )
    )
    this.setState({
      stores,
      searchValue,
      giftCardFilter,
      afterpayFilter,
      retailCategory,
      subRetailCategory,
    })
  }

  storeInCategory = (storeID, slug) =>
    this.props.centreCategories.storeIDsOf(slug).includes(storeID)

  storeMatchesSearch = (searchValue, stores) => {
    if (searchValue.length < 1) {
      return stores
    }

    const options = {
      shouldSort: true,
      threshold: 0.3,
      location: 0,
      keys: ['title'],
    }

    return new Fuse(stores, options).search(searchValue)
  }

  calculateCategories = category => {
    const { centreCategories } = this.props

    // reset everything
    if (!(category && centreCategories.exists(category))) {
      return {
        category: null,
        retailCategory: '',
        subRetailCategory: '',
      }
    }

    const parents = centreCategories.parentsOf(category).map(c => c.slug)

    // just a top-level category
    if (!parents.length) {
      return {
        category,
        retailCategory: category,
        subRetailCategory: category,
      }
    }

    // some sub-category with a parent category above it
    return {
      category,
      retailCategory: parents[0],
      subRetailCategory: category,
    }
  }

  handleGiftCard = giftCardFilter => {
    this.filterList({ giftCardFilter })
    this.analyticsAction(analyticsUtils.actions.STORE_GIFTCARD_FILTER)
  }
  handleAfterPay = afterpayFilter => {
    this.filterList({ afterpayFilter })
    this.analyticsAction(analyticsUtils.actions.STORE_AFTERPAY_FILTER)
  }
  clearSearch = () => {
    this.filterList({
      searchValue: '',
    })
  }

  renderSelectOption = ({ option, onClick }) => {
    const handleClick = () => {
      onClick()
      this.setState({ option })
      this.clearSearch()
    }

    return (
      <Link
        to={this.createAddress({ category: option.value })}
        className={style.selectLink}
        onClick={handleClick}
      >
        {option.label}
      </Link>
    )
  }

  getSearchValue = () => (this.searchField ? this.searchField.value : '')

  handleOnChangeSearch = debounce(searchTerm => {
    this.setState({ afterpayFilter: false, giftCardFilter: false })
    const searchValue = this.getSearchValue()
    this.filterList({
      searchValue,
      category: null,
    })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.STORE_SEARCH,
      eventAction: analyticsUtils.actions.SEARCH_SHOWN,
      eventLabel: searchValue,
      noReturnedResults: this.state.stores.length,
    })
  }, 250)

  stickyHeaderEnable = isMobile => {
    if (isMobile) {
      Header.toggleStickiness(true)
    }
  }
  stickyHeaderDisable = (e, isMobile) => {
    e.preventDefault()
    if (isMobile) {
      Header.toggleStickiness(false)
      document.getElementById('searchInp').scrollIntoView()
    }
    this.analyticsAction(analyticsUtils.actions.CLICK_TO_START_SEARCH)
  }
  listViewToggle = () => {
    this.props.toggle()
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.USER_CLICK,
      eventCategory: analyticsUtils.categories.SETTINGS_CLICK,
      eventAction: analyticsUtils.actions.STORE_DISPLAY_GRID_LIST,
    })
  }
  setHeading() {
    const subCategories = this.getSubcategories()
    const { subRetailCategory } = this.state
    let selectedCategory = this.props.centreCategories.root.find(
      x => x.slug === subRetailCategory
    )
    if (selectedCategory) {
      return `${selectedCategory.title} stores`
    } else if (subCategories) {
      selectedCategory = subCategories.find(x => x.slug === subRetailCategory)
      if (selectedCategory) {
        return `${selectedCategory.title} stores`
      }
    }
    return 'Browse stores'
  }

  getSubcategories() {
    return this.props.centreCategories
      .subcategoriesOf(this.state.retailCategory)
      .sort((a, b) => a.title.localeCompare(b.title))
  }

  render() {
    const { centreName, centreCategories, isListView } = this.props
    const {
      retailCategory,
      searchValue,
      option,
      afterpayFilter,
      giftCardFilter,
      stores,
    } = this.state
    const visibleStores = stores

    const subCategories = this.getSubcategories()
    const disableSubCategory = !retailCategory || subCategories.length < 2
    // don't show a subcategory dropdown if there's only 1 option
    const subCategoryStyle =
      retailCategory.length > 0 && subCategories.length !== 0
        ? style.subCategory
        : style.subCategoryHidden
    return (
      <AgentInfoContext.Consumer>
        {({ isMobile }) => (
          <Container type="large" className={style.container}>
            <Heading data-test-id="browse-stores-heading">
              {this.setHeading(subCategories)}
            </Heading>
            <div className={style.searchBar}>
              <SearchField
                id="searchInp"
                value={this.getSearchValue()}
                placeholder={`Find stores in ${centreName}`}
                autoCorrect="off"
                autoComplete="off"
                innerRef={elem => (this.searchField = elem)}
                onChange={this.handleOnChangeSearch}
                onFocus={e => this.stickyHeaderDisable(e, isMobile)}
                onBlur={() => this.stickyHeaderEnable(isMobile)}
              />
              <Select
                buttonDataTestId="store-category-button"
                dataTestId="store-category"
                label={optionLabel}
                active={({ value }) => value === retailCategory}
                options={[
                  { label: 'Choose category', value: '' },
                  ...centreCategories.root.map(categoryToOption),
                ]}
              >
                {this.renderSelectOption}
              </Select>
              <span className={subCategoryStyle}>
                <Select
                  buttonDataTestId="store-subcategory-button"
                  dataTestId="store-subcategory"
                  disabled={disableSubCategory}
                  label={optionLabel}
                  active={({ value }) => value === this.state.subRetailCategory}
                  options={[
                    { label: 'Choose subcategory', value: retailCategory },
                    ...subCategories.map(categoryToOption),
                  ]}
                >
                  {this.renderSelectOption}
                </Select>
              </span>
            </div>
            <div className={style.itemsInfoWrapper}>
              <div>
                <Checkbox
                  label="Gift cards"
                  checked={giftCardFilter}
                  classNames={{
                    label: style.checkboxLabel,
                  }}
                  onChange={e => this.handleGiftCard(!giftCardFilter)}
                />
                <span className={style.checkboxSpacer} />
              </div>
              <button
                data-test-id="toggle-view"
                className={style.toggleWrapper}
                onClick={this.listViewToggle}
              >
                <Icon
                  type="grid-view"
                  className={classNames(style.toggle, {
                    [style.toggleInactive]: isListView,
                  })}
                />
                <Icon
                  type="list-view"
                  className={classNames(style.toggle, {
                    [style.toggleInactive]: !isListView,
                  })}
                />
              </button>
            </div>
            <VisibleStores
              stores={visibleStores}
              searchValue={searchValue}
              isListView={this.props.isListView}
              openLate={afterpayFilter}
              option={option}
            />
          </Container>
        )}
      </AgentInfoContext.Consumer>
    )
  }
}

StoreList.propTypes = {
  stores: PropTypes.array.isRequired,
  centreName: PropTypes.string.isRequired,
  createAddress: PropTypes.func.isRequired,
  category: PropTypes.string,
  filters: PropTypes.shape({
    'gift-cards-accepted': PropTypes.any,
    'after-pay': PropTypes.any,
  }).isRequired,
  centreCategories: PropTypes.shape({
    exists: PropTypes.func.isRequired,
    root: PropTypes.array.isRequired,
    childrenOf: PropTypes.func.isRequired,
    parentsOf: PropTypes.func.isRequired,
    subcategoriesOf: PropTypes.func.isRequired,
    storeIDsOf: PropTypes.func.isRequired,
    pathOf: PropTypes.func.isRequired,
  }).isRequired,
  isListView: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
}

StoreList.defaultProps = {
  isListView: false,
}

export default StoreList
